import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileText, ChevronLeft, ChevronRight, RefreshCw, ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";

export interface ExpandableRowData<T> {
  id: string;
  title: string;
  subtitle?: string;
  badge?: {
    label: string;
    color: string;
  };
  badges?: {
    label: string;
    color: string;
  }[];
  progress?: {
    current: number;
    total: number;
  };
  metadata?: string;
  detailRows: T[];
  data: any;
}

export interface DetailColumn<T> {
  key: string;
  header: string | ((rowData: any) => string);
  align?: "left" | "center" | "right";
  render: (item: T) => React.ReactNode;
}

export interface DataTableExpandableProps<T> {
  rows: ExpandableRowData<T>[];
  detailColumns: DetailColumn<T>[];
  expandedRowIds: Set<string>;
  onToggleRow: (rowId: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  filterOptions?: { value: string; label: string }[];
  filterPlaceholder?: string;
  emptyMessage?: string;
  emptyMessageWithFilters?: string;
  showSearch?: boolean;
  showFilter?: boolean;
  loading?: boolean;
  onRefresh?: () => void;
  // Pagination
  showPagination?: boolean;
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  // Actions
  primaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  getRowKey: (item: ExpandableRowData<T>) => string;
}

export function DataTableExpandable<T>({
  rows,
  detailColumns,
  expandedRowIds,
  onToggleRow,
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  filterValue = "all",
  onFilterChange,
  filterOptions = [],
  filterPlaceholder = "Filter",
  emptyMessage = "No data available",
  emptyMessageWithFilters = "No items found",
  showSearch = true,
  showFilter = false,
  loading = false,
  onRefresh,
  showPagination = false,
  currentPage = 1,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  primaryAction,
  secondaryAction,
  getRowKey,
}: DataTableExpandableProps<T>) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasFilters = searchQuery || filterValue !== "all";

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      {(showSearch || showFilter || primaryAction || secondaryAction || onRefresh) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {/* Search and Filter */}
          <div className="flex gap-3 flex-1 w-full sm:w-auto">
            {showSearch && onSearchChange && (
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            )}
            {showFilter && onFilterChange && filterOptions.length > 0 && (
              <Select value={filterValue} onValueChange={onFilterChange} disabled={loading}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder={filterPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {onRefresh && (
              <Button onClick={onRefresh} size="sm" variant="outline" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
            {secondaryAction && (
              <Button onClick={secondaryAction.onClick} size="sm" variant="outline" disabled={loading}>
                {secondaryAction.icon}
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button onClick={primaryAction.onClick} size="sm" disabled={loading}>
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-3" />
            <p className="text-lg font-medium mb-2 text-gray-900 dark:text-gray-100">
              {hasFilters ? emptyMessageWithFilters : emptyMessage}
            </p>
            {hasFilters ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Try adjusting your search or filter criteria
              </p>
            ) : primaryAction ? (
              <div className="mt-4">
                <Button onClick={primaryAction.onClick}>
                  {primaryAction.icon}
                  {primaryAction.label}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row, rowIndex) => {
              const isExpanded = expandedRowIds.has(row.id);
              const progressPercent = row.progress && row.progress.total > 0
                ? (row.progress.current / row.progress.total) * 100
                : 0;

              return (
                <div key={getRowKey(row)} className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
                  {/* Expandable Row Header */}
                  <div
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors gap-4"
                    onClick={() => onToggleRow(row.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                            {rowIndex + 1}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 break-words line-clamp-2">
                            {row.title}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            {row.badges ? (
                              row.badges.map((badge, index) => (
                                <span key={index} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${badge.color}`}>
                                  {badge.label}
                                </span>
                              ))
                            ) : row.badge ? (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${row.badge.color}`}>
                                {row.badge.label}
                              </span>
                            ) : null}
                          </div>
                          {row.subtitle && (
                            <span className="text-sm text-gray-600 dark:text-gray-400 w-full">
                              {row.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-auto">
                      {row.progress && (
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {row.progress.current}/{row.progress.total}
                          </div>
                          <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                            <div
                              className="bg-emerald-500 h-2 rounded-full transition-all"
                              style={{ width: `${progressPercent}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      {row.metadata && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {row.metadata}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Detail Table */}
                  {isExpanded && row.detailRows.length > 0 && (
                    <div className="border-t dark:border-gray-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800">
                            <TableHead className="font-semibold text-center w-16">#</TableHead>
                            {detailColumns.map((col) => (
                              <TableHead
                                key={col.key}
                                className={`font-semibold ${
                                  col.align === "right"
                                    ? "text-right"
                                    : col.align === "center"
                                    ? "text-center"
                                    : "text-left"
                                }`}
                              >
                                {typeof col.header === 'function' ? col.header(row.data) : col.header}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {row.detailRows.map((detail, idx) => (
                            <TableRow key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <TableCell className="text-center font-medium text-gray-500 dark:text-gray-400 text-sm w-16">
                                {idx + 1}
                              </TableCell>
                              {detailColumns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className={
                                    col.align === "right"
                                      ? "text-right"
                                      : col.align === "center"
                                      ? "text-center"
                                      : "text-left"
                                  }
                                >
                                  {col.render(detail)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {showPagination && totalPages > 0 && onPageChange && onPageSizeChange && (
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 px-2 py-3 bg-gray-50/50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Left: Info & Refresh */}
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full lg:w-auto">
            {onRefresh && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={loading}
                  className="h-9 px-4 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                  title="Refresh data"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  <span className="font-medium">Refresh</span>
                </Button>
                <div className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-600"></div>
              </>
            )}
            
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <span className="text-gray-500 dark:text-gray-500">Page</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100 px-2 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                  {currentPage}
                </span>
                <span className="text-gray-500 dark:text-gray-500">of</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{totalPages}</span>
              </div>
              
              <div className="hidden sm:block h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
              
              <div className="text-gray-600 dark:text-gray-400 text-center sm:text-left">
                <span className="text-gray-500 dark:text-gray-500">Showing </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{(currentPage - 1) * pageSize + 1}</span>
                <span className="text-gray-500 dark:text-gray-500"> to </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{Math.min(currentPage * pageSize, totalCount)}</span>
                <span className="text-gray-500 dark:text-gray-500"> of </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{totalCount}</span>
              </div>
            </div>
          </div>

          {/* Right: Page Size & Navigation */}
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Rows per page:</span>
              <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(Number(value))}>
                <SelectTrigger className="w-[75px] h-9 font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="hidden md:flex items-center gap-1">
                {(() => {
                  let start = Math.max(1, currentPage - 2);
                  let end = Math.min(totalPages, currentPage + 2);
                  if (currentPage <= 3) {
                    start = 1;
                    end = Math.min(5, totalPages);
                  } else if (currentPage >= totalPages - 2) {
                    start = Math.max(1, totalPages - 4);
                    end = totalPages;
                  }
                  const pageNumbers: (number | string)[] = [];
                  for (let i = start; i <= end; i++) pageNumbers.push(i);
                  if (start > 1) {
                    pageNumbers.unshift(1);
                    if (start > 2) pageNumbers.splice(1, 0, "...");
                  }
                  if (end < totalPages) {
                    if (end < totalPages - 1) pageNumbers.push("...");
                    pageNumbers.push(totalPages);
                  }
                  return pageNumbers.map((p, idx) =>
                    p === "..." ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 dark:text-gray-500">...</span>
                    ) : (
                      <Button
                        key={`page-${p}`}
                        variant={currentPage === p ? "default" : "outline"}
                        size="sm"
                        onClick={() => onPageChange(p as number)}
                        className={`h-9 w-9 p-0 font-medium transition-all ${
                          currentPage === p 
                            ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm" 
                            : "hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        }`}
                      >
                        {p}
                      </Button>
                    )
                  );
                })()}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
